import {
  queryOptions,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient
} from '@tanstack/react-query'
import { components } from '../types/prove-v1'
import { TeamResponse } from '../types/types'
import { useAuth } from './useAuth'
import { OwnerId } from '../utils/taskUtils'
import { throwOnError } from '../utils/throwOnError'

type AuthFetch = ReturnType<typeof useAuth>['authFetch']
export type SaveTeam = components['schemas']['SaveTeam']

/**
 * Options for å hente team som hører til en seksjon eller avdeling
 */
const getTeamOptions = (
  authFetch: AuthFetch,
  seksjonId?: string,
  avdelingId?: string
) => {
  return queryOptions({
    queryKey: ['team', seksjonId, avdelingId],
    staleTime: 1000 * 60 * 60 * 24, // 1 dag
    enabled: !!seksjonId || !!avdelingId,
    queryFn: async () => {
      if (seksjonId) {
        const { data, error } = await authFetch().GET(
          '/orgenhet-api/seksjoner/{seksjonid}/team',
          {
            params: {
              path: {
                seksjonid: seksjonId
              }
            }
          }
        )

        if (error) {
          throw new Error('Kunne ikke hente team')
        }

        const response: TeamResponse = Array.isArray(data)
          ? data.map((team) => ({
              ...team,
              id: String(team.id)
            }))
          : []
        return response ?? []
      }

      if (avdelingId) {
        const { data, error } = await authFetch().GET('/orgenhet-api/team')

        if (error) {
          throw new Error('Kunne ikke hente team')
        }

        const response: TeamResponse = Array.isArray(data)
          ? data
              .filter((team) => team.avdelingId === avdelingId)
              .map((team) => ({
                ...team,
                id: String(team.id)
              }))
          : []
        return response ?? []
      }

      return []
    }
  })
}

export const GetTeamOptions = (
  authFetch: AuthFetch,
  seksjonId?: string,
  avdelingId?: string
) => {
  return getTeamOptions(authFetch, seksjonId, avdelingId)
}

/**
 * Bruk når du kun skal hente team for én seksjon eller avdeling
 */
export const useGetTeamQuery = (seksjonId?: string, avdelingId?: string) => {
  const { authFetch } = useAuth()
  return useQuery(GetTeamOptions(authFetch, seksjonId, avdelingId))
}

export const getAllTeamsOptions = (authFetch: AuthFetch) =>
  queryOptions({
    queryKey: ['teams'],
    staleTime: 1000 * 60 * 60 * 24, // Imperative subtree fetches reuse this cache.
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
    queryFn: async () => {
      const { data, error } = await authFetch().GET('/orgenhet-api/team')
      if (error) {
        throwOnError(error)
      }
      return Array.isArray(data)
        ? data.map((team) => ({
            ...team,
            id: String(team.id)
          }))
        : []
    }
  })

export const useGetAllTeamsQuery = (enabled = true) => {
  const { authFetch } = useAuth()
  return useQuery({
    ...getAllTeamsOptions(authFetch),
    enabled
  })
}

export const useCreateTeamMutation = () => {
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async (team: SaveTeam) => {
      const { data, error } = await authFetch().POST('/orgenhet-api/team', {
        body: team
      })
      if (error) {
        throwOnError(error)
      }
      return data
    },
    onSuccess: async (_, team) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['teams'] }),
        queryClient.invalidateQueries({ queryKey: ['team'] }),
        team.seksjonId
          ? queryClient.invalidateQueries({
              queryKey: ['team', team.seksjonId]
            })
          : Promise.resolve(),
        team.avdelingId
          ? queryClient.invalidateQueries({
              queryKey: ['team', undefined, team.avdelingId]
            })
          : Promise.resolve()
      ])
    }
  })
}

export const useUpdateTeamMutation = () => {
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async ({ id, ...team }: SaveTeam & { id: string }) => {
      const { data, error } = await authFetch().PUT(
        '/orgenhet-api/team/{teamid}',
        {
          params: { path: { teamid: Number(id) } },
          body: team
        }
      )
      if (error) {
        throwOnError(error)
      }
      return data
    },
    onSuccess: async (_, team) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['teams'] }),
        queryClient.invalidateQueries({ queryKey: ['team'] }),
        team.seksjonId
          ? queryClient.invalidateQueries({
              queryKey: ['team', team.seksjonId]
            })
          : Promise.resolve(),
        team.avdelingId
          ? queryClient.invalidateQueries({
              queryKey: ['team', undefined, team.avdelingId]
            })
          : Promise.resolve()
      ])
    }
  })
}

export const useDeleteTeamMutation = () => {
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async (team: {
      id: string
      seksjonId?: string
      avdelingId?: string
    }) => {
      const { error } = await authFetch().DELETE(
        '/orgenhet-api/team/{teamid}',
        {
          params: { path: { teamid: Number(team.id) } }
        }
      )
      if (error) {
        throwOnError(error)
      }
    },
    onSuccess: async (_, team) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['teams'] }),
        queryClient.invalidateQueries({ queryKey: ['team'] }),
        team.seksjonId
          ? queryClient.invalidateQueries({
              queryKey: ['team', team.seksjonId]
            })
          : Promise.resolve(),
        team.avdelingId
          ? queryClient.invalidateQueries({
              queryKey: ['team', undefined, team.avdelingId]
            })
          : Promise.resolve()
      ])
    }
  })
}

export const useGetTeamQueries = (seksjonOwnerIds: OwnerId[]) => {
  const { authFetch } = useAuth()
  return useQueries({
    queries: seksjonOwnerIds.map((ownerId) =>
      getTeamOptions(authFetch, ownerId.owner)
    ),
    combine: (results) => ({
      data: results.flatMap((result) =>
        Array.isArray(result.data) ? result.data : []
      ),
      isLoading: results.some((result) => result.isLoading),
      isError: results.some((result) => result.isError),
      error: results.find((result) => result.error != null)?.error
    })
  })
}
